import { localStorage } from "../helpers/localStorage"
import { OpenAPI, User, UsersService } from "../sdk"
import { createContext, ReactElement, ReactNode, SetStateAction, useCallback, useEffect, useRef, useState } from "react"

type LoginData = {
  jwt: string
  user: User
}
export type AuthContextType = {
  onFirstLoad: boolean
  user?: User | null
  setUser(user: SetStateAction<User | null | undefined>): void
  logout(): void
  onLogin(data: LoginData): void
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export default function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<User | null>()
  const onFirstLoad = useRef<boolean>(true)

  const onLogin = useCallback(({ jwt, user }: LoginData): void => {
    localStorage.setItem("user", { jwt, id: user.id })
    OpenAPI.TOKEN = jwt
    onFirstLoad.current = false
    setUser(user)
  }, [])

  const logout = useCallback((): void => {
    localStorage.removeItem("user")
    OpenAPI.TOKEN = undefined
    onFirstLoad.current = false
    setUser(null)
  }, [])

  useEffect(() => {
    const storageData = localStorage.getItem("user")

    if (storageData) {
      void (async(): Promise<void> => {
        try {
          OpenAPI.TOKEN = storageData.jwt
          const { data: jwt } = await UsersService.refreshToken({
            handler: {
              userId: storageData.id
            }
          })

          OpenAPI.TOKEN = jwt
          const { data } = await UsersService.getUser({
            handler: {
              userId: storageData.id
            }
          })

          if ((data as User).admin) {
            onLogin({ jwt, user: data as User })
          } else {
            throw Error()
          }
        } catch (err) {
          logout()
        }
      })()
    } else {
      logout()
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        onFirstLoad: onFirstLoad.current,
        user,
        setUser,
        onLogin,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
